import React from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { IconGrid } from '../../ui/IconGrid/IconGrid';
import { useApi } from '../../../hooks/useApi';
import { PageData, ContentSection, QuickLinkItem, RegionItem } from '../../../types/api';
import { LOADING_MESSAGES, ERROR_MESSAGES } from '../../../utils/constants';
import { apiHelpers } from '../../../services/api';

const Container = styled.div`
  max-width: ${({ theme }) => theme.breakpoints.wide};
  margin: 0 auto;
  padding: 0 ${({ theme }) => theme.spacing.sm};
`;

const Section = styled.section`
  margin: ${({ theme }) => theme.spacing.xl} 0;
`;

const SectionTitle = styled.h1`
  text-align: center;
  margin-bottom: ${({ theme }) => theme.spacing.lg};
`;

const WelcomeContent = styled.div`
  text-align: center;
  font-size: 1.1rem;
  margin-bottom: ${({ theme }) => theme.spacing.xl};
  
  p {
    margin-bottom: ${({ theme }) => theme.spacing.md};
  }
`;

const AboutContent = styled.div`
  font-size: 1rem;
  line-height: 1.6;
  
  p {
    margin-bottom: ${({ theme }) => theme.spacing.md};
  }
  
  a {
    color: ${({ theme }) => theme.colors.accent};
    
    &:hover {
      text-decoration: underline;
    }
  }
`;

const RegionsContainer = styled.div`
  display: flex;
  justify-content: center;
  gap: ${({ theme }) => theme.spacing.lg};
  flex-wrap: wrap;
`;

const RegionContainer = styled.div`
  text-align: center;
`;

const RegionLink = styled(Link)`
  display: block;
  text-decoration: none;
  color: inherit;
  transition: ${({ theme }) => theme.transitions.fast};
  
  &:hover img {
    transform: scale(1.05);
  }
  
  &:focus {
    outline: 2px solid ${({ theme }) => theme.colors.accent};
    outline-offset: 2px;
    border-radius: 4px;
  }
`;

const RegionImage = styled.img`
  width: 160px;
  height: 202px;
  object-fit: contain;
  transition: ${({ theme }) => theme.transitions.fast};
`;

const FacebookSection = styled.div`
  text-align: center;
`;

const FacebookLink = styled.a`
  display: inline-block;
  text-decoration: none;
  transition: ${({ theme }) => theme.transitions.fast};
  
  &:hover img {
    transform: scale(1.05);
  }
  
  &:focus {
    outline: 2px solid ${({ theme }) => theme.colors.accent};
    outline-offset: 2px;
    border-radius: 4px;
  }
`;

const FacebookImage = styled.img`
  width: 160px;
  height: 202px;
  object-fit: contain;
  transition: ${({ theme }) => theme.transitions.fast};
`;

const LoadingContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${({ theme }) => theme.spacing.xxl};
  color: ${({ theme }) => theme.colors.textLight};
`;

const ErrorContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${({ theme }) => theme.spacing.xxl};
  color: #d32f2f;
  background-color: #ffebee;
  border: 1px solid #ffcdd2;
  border-radius: 4px;
  margin: ${({ theme }) => theme.spacing.md} 0;
`;

export const HomePage: React.FC = () => {
  const { data: pageData, loading, error } = useApi<PageData>('/pages/home.json');

  if (loading) {
    return (
      <Container>
        <LoadingContainer>{LOADING_MESSAGES.PAGE}</LoadingContainer>
      </Container>
    );
  }

  if (error) {
    return (
      <Container>
        <ErrorContainer>
          {ERROR_MESSAGES.LOADING_ERROR}: {error}
        </ErrorContainer>
      </Container>
    );
  }

  if (!pageData) {
    return (
      <Container>
        <ErrorContainer>{ERROR_MESSAGES.PAGE_NOT_FOUND}</ErrorContainer>
      </Container>
    );
  }

  const content = pageData.content as any; // Type assertion for home page content
  const sections = content.sections || [];

  const welcomeSection = sections.find((s: ContentSection) => s.type === 'welcome');
  const quickLinksSection = sections.find((s: ContentSection) => s.type === 'quickLinks');
  const aboutSection = sections.find((s: ContentSection) => s.type === 'about');
  const regionsSection = sections.find((s: ContentSection) => s.type === 'regions');
  const socialSection = sections.find((s: ContentSection) => s.type === 'social');
  const thanksSection = sections.find((s: ContentSection) => s.type === 'thanks');

  return (
    <Container>
      {welcomeSection && (
        <Section>
          <WelcomeContent 
            dangerouslySetInnerHTML={{ __html: welcomeSection.content || '' }} 
          />
        </Section>
      )}

      {quickLinksSection && quickLinksSection.items && (
        <Section>
          <SectionTitle>{quickLinksSection.title}</SectionTitle>
          <IconGrid items={quickLinksSection.items as QuickLinkItem[]} />
        </Section>
      )}

      <hr />

      {aboutSection && (
        <Section>
          <SectionTitle>{aboutSection.title}</SectionTitle>
          <AboutContent 
            dangerouslySetInnerHTML={{ __html: aboutSection.content || '' }} 
          />
        </Section>
      )}

      <hr />

      {regionsSection && regionsSection.items && (
        <Section>
          <SectionTitle>{regionsSection.title}</SectionTitle>
          <RegionsContainer>
            {(regionsSection.items as RegionItem[]).map((region, index) => (
              <RegionContainer key={`region-${index}-${region.name.replace(/\s+/g, '-').toLowerCase()}`}>
                {region.link ? (
                  <RegionLink to={region.link}>
                    <RegionImage 
                      src={apiHelpers.buildAssetUrl(region.image)} 
                      alt={region.alt} 
                      loading="lazy"
                    />
                  </RegionLink>
                ) : (
                  <RegionImage 
                    src={apiHelpers.buildAssetUrl(region.image)} 
                    alt={region.alt} 
                    loading="lazy"
                  />
                )}
              </RegionContainer>
            ))}
          </RegionsContainer>
        </Section>
      )}

      <hr />

      {socialSection && (
        <Section>
          <SectionTitle>{socialSection.title}</SectionTitle>
          <FacebookSection>
            <FacebookLink 
              href={socialSection.link || '#'} 
              target="_blank" 
              rel="noopener noreferrer"
              aria-label={`${socialSection.title} (opens in new window)`}
            >
              <FacebookImage 
                src={apiHelpers.buildAssetUrl(socialSection.image || '')} 
                alt={socialSection.alt || 'Facebook'} 
                loading="lazy"
              />
            </FacebookLink>
          </FacebookSection>
        </Section>
      )}

      <hr />

      {thanksSection && (
        <Section>
          <SectionTitle>{thanksSection.title}</SectionTitle>
          <AboutContent 
            dangerouslySetInnerHTML={{ __html: thanksSection.content || '' }} 
          />
        </Section>
      )}


    </Container>
  );
};

export default HomePage;
